import React, { useState, useEffect, useCallback } from 'react';
import { getProducts, updateProduct, addProduct, deleteProduct, getToken } from '../api/client';

const BAND_BG = { dg:'#0d3320', g:'#d1fae5', y:'#fef9c3', r:'#fee2e2' };
const BAND_FG = { dg:'#4ade80', g:'#065f46', y:'#854d0e', r:'#991b1b' };
const PILL_BG = { dg:'rgba(74,222,128,.2)', g:'#d1fae5', y:'#fef9c3', r:'#fee2e2' };
const PILL_FG = { dg:'#4ade80', g:'#065f46', y:'#854d0e', r:'#991b1b' };
const DARK_BAND_BG = { dg:'#0d2a1a', g:'#0a2016', y:'#1c1500', r:'#200808' };
const DARK_BAND_FG = { dg:'#4ade80', g:'#6ee7b7', y:'#fcd34d', r:'#fca5a5' };
const DARK_PILL_BG = { dg:'rgba(74,222,128,.18)', g:'rgba(110,231,183,.18)', y:'rgba(252,211,77,.15)', r:'rgba(252,165,165,.15)' };
const DARK_PILL_FG = { dg:'#4ade80', g:'#6ee7b7', y:'#fcd34d', r:'#fca5a5' };

const fI = n => n > 0 ? '₹' + Math.round(n).toLocaleString('en-IN') : '—';
const fU = n => n > 0 ? '$' + n.toFixed(2) : '—';
const fW = n => n > 0 ? n.toFixed(2) + ' kg' : '—';
const fP = n => n.toFixed(1) + '%';

const COLOURS = [
  'Space Grey','Space Gray','Rose Gold','Deep Purple','Light Grey','Dark Grey',
  'Midnight Black','Midnight Blue','Pearl White','Jet Black',
  'Black','White','Silver','Gold','Blue','Red','Green','Yellow','Orange',
  'Purple','Pink','Grey','Gray','Brown','Beige','Cyan','Violet','Midnight',
  'Starlight','Graphite','Platinum','Natural','Coral','Teal','Navy','Cream',
  'Sand','Olive','Indigo','Lavender','Copper','Bronze','Titanium','Charcoal',
  'Slate','Pearl','Sage','Mint','Khaki','Ivory','Maroon',
];
const extractColour = name => { for (const col of COLOURS) { if (new RegExp('\\b'+col+'\\b','i').test(name)) return col; } return null; };
const getColour = p => p.colour || extractColour(p.name) || '—';

const HEADERS = [
  {l:'', w:26},
  {l:'', w:30},
  {l:'Product', w:200},
  {l:'ASIN', w:90},
  {l:'Colour', w:90},
  {l:'Amazon Price', w:90, g:'price'},
  {l:'Ex-GST', w:90, g:'price'},
  {l:'Wt Packed', w:75, g:'price'},
  {l:'Wt Chargeable', w:75, g:'price'},
  {l:'Local Freight', w:80, g:'price'},
  {l:'Export Freight', w:80, g:'price'},
  {l:'Export Expenses', w:90, g:'price'},
  {l:'Total Export', w:80, g:'price'},
  {l:'Drawback', w:75, g:'ben'},
  {l:'RoDTEP', w:75, g:'ben'},
  {l:'Aff CB', w:70, g:'ben', admin:true},
  {l:'Card CB', w:70, g:'ben', admin:true},
  {l:'Purc+Exp-Bft', w:95, g:'mar'},
  {l:'Buyer Price', w:90, g:'price'},
  {l:'Net Margin', w:85, g:'mar'},
  {l:'Net Margin (USD)', w:85, g:'mar'},
  {l:'Margin %', w:70, g:'mar'},
  {l:'Actions', w:90},
];

export default function Dashboard({ role, dark }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [chartProduct, setChartProduct] = React.useState(null);
  const [chartData, setChartData] = React.useState([]);
  const [chartLoading, setChartLoading] = React.useState(false);
  const openChart = async (p) => {
    setChartProduct(p); setChartLoading(true);
    try {
      const token = getToken();
      const res = await fetch('/api/products/history/' + p.id, {headers:{Authorization:'Bearer '+token}});
      const data = await res.json();
      setChartData(data.reverse());
    } catch(e) { setChartData([]); }
    setChartLoading(false);
  };
  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');
  const [refreshed, setRefreshed] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getProducts();
      setProducts(res.data);
      setLastUpdated(new Date().toLocaleTimeString('en-IN'));
      setRefreshed(true); setTimeout(() => setRefreshed(false), 2000);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const handleAdd = async () => {
    const asin = newUrl.trim().toUpperCase();
    if (!/^[A-Z0-9]{10}$/.test(asin)) { alert('Enter a valid 10-character Amazon ASIN (e.g. B0F3QJLD3B)'); return; }
    setAdding(true);
    try { await addProduct(asin, `https://www.amazon.in/dp/${asin}`); setNewUrl(''); setShowAdd(false); await load(); }
    catch(e) { alert(e.response?.data?.detail || 'Error adding product'); }
    finally { setAdding(false); }
  };

  const toggleDash = async p => { await updateProduct(p.id, {dash_visible:!p.dash_visible}); await load(); };
  const doDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try { await deleteProduct(confirmDel.id); setConfirmDel(null); await load(); }
    catch(e) { alert(e.response?.data?.detail || 'Remove failed'); }
    finally { setDeleting(false); }
  };
  const togglePin  = async p => { await updateProduct(p.id, {pinned:!p.pinned, pin_order:p.pinned?0:Date.now()}); await load(); };
  const saveSRP    = async (p, val) => { if (!val || isNaN(val)) return; await updateProduct(p.id, {srp_usd:parseFloat(val)}); await load(); };

  const sorted = () => {
    const vis = products.filter(p => p.master_visible);
    const q = search.toLowerCase();
    const f = q ? vis.filter(p => p.name.toLowerCase().includes(q) || p.asin.toLowerCase().includes(q)) : vis;
    const pinned   = f.filter(p => p.pinned && p.dash_visible && p.in_stock);
    const active   = f.filter(p => !p.pinned && p.dash_visible && p.in_stock);
    const oosRaw   = f.filter(p => p.dash_visible && !p.in_stock);
    const oos      = [...oosRaw].sort((a,b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      if (a.pinned && b.pinned) return (b.pin_order||0)-(a.pin_order||0);
      return 0;
    });
    const inactive = f.filter(p => !p.dash_visible);
    const byMar = arr => [...arr].sort((a,b) => (b.calculations?.net_margin_pct||0)-(a.calculations?.net_margin_pct||0));
    const pSorted = [...pinned].sort((a,b) => (b.pin_order||0)-(a.pin_order||0));
    const dg = byMar(active.filter(p => (p.calculations?.net_margin_pct||0) >= 8));
    const g  = byMar(active.filter(p => { const x=p.calculations?.net_margin_pct||0; return x>=4&&x<8; }));
    const y  = byMar(active.filter(p => { const x=p.calculations?.net_margin_pct||0; return x>=0&&x<4; }));
    const r  = byMar(active.filter(p => (p.calculations?.net_margin_pct||0) < 0));
    return [
      {label:'Pinned', rows:pSorted, type:'pinned'},
      {label:'Above 8% margin', rows:dg, type:'dg'},
      {label:'4-8% margin', rows:g, type:'g'},
      {label:'0-4% margin', rows:y, type:'y'},
      {label:'Negative margin', rows:r, type:'r'},
      {label:'Out of stock', rows:oos, type:'oos'},
      {label:'Paused', rows:inactive, type:'inactive'},
    ];
  };

  const forex = products[0]?.forex;
  const thBg = g => g==='price'?'rgba(59,130,246,.04)':g==='ben'?'rgba(245,158,11,.05)':g==='mar'?'rgba(16,185,129,.05)':'var(--surface2,#f0efe9)';
  const bBg = dark ? DARK_BAND_BG : BAND_BG;
  const bFg = dark ? DARK_BAND_FG : BAND_FG;
  const pBg = dark ? DARK_PILL_BG : PILL_BG;
  const pFg = dark ? DARK_PILL_FG : PILL_FG;

  if (loading) return <div style={{padding:40,textAlign:'center',color:'#9b9890'}}>Loading live data...</div>;

  const ChartModal = () => {
    const [source, setSource] = React.useState('keepa');
    const [keepaData, setKeepaData] = React.useState([]);
    const [keepaLoading, setKeepaLoading] = React.useState(false);
    const [range, setRange] = React.useState('3M');
    const [hov, setHov] = React.useState(null);
    const [hovX, setHovX] = React.useState(null);

    React.useEffect(() => {
      if (!chartProduct) return;
      setSource('keepa'); setRange('3M'); setHov(null); setKeepaData([]); setKeepaLoading(true);
      const token = getToken();
      fetch('/api/products/keepa-history/' + chartProduct.id,
        {headers:{Authorization:'Bearer '+token}})
        .then(r=>r.json())
        .then(d=>{setKeepaData(Array.isArray(d)?d:[]); setKeepaLoading(false);})
        .catch(()=>setKeepaLoading(false));
    }, [chartProduct?.id]);

    const doRefresh = () => {
      setHov(null); setHovX(null);
      if (source === 'keepa') {
        setKeepaData([]); setKeepaLoading(true);
        const token = getToken();
        fetch('/api/products/keepa-history/' + chartProduct.id,
          {headers:{Authorization:'Bearer '+token}})
          .then(r=>r.json()).then(d=>{setKeepaData(Array.isArray(d)?d:[]); setKeepaLoading(false);})
          .catch(()=>setKeepaLoading(false));
      } else {
        setChartLoading(true);
        const token = getToken();
        fetch('/api/products/history/' + chartProduct.id,
          {headers:{Authorization:'Bearer '+token}})
          .then(r=>r.json()).then(d=>{setChartData(d.reverse()); setChartLoading(false);})
          .catch(()=>setChartLoading(false));
      }
    };

    if (!chartProduct) return null;

    const RANGES = [{k:'1D',d:1},{k:'7D',d:7},{k:'1M',d:30},{k:'3M',d:90},{k:'6M',d:180},{k:'1Y',d:365}];
    const rangeDays = RANGES.find(r=>r.k===range)?.d || 90;
    const cutoff = Date.now() - rangeDays*86400000;
    const rawAll = (source==='keepa' ? keepaData : [...chartData]).filter(d => d.price > 0);
    const inRange = rawAll.filter(d => new Date(d.recorded_at).getTime() >= cutoff);
    const beforeRange = rawAll.filter(d => new Date(d.recorded_at).getTime() < cutoff);
    let pts = inRange;
    if (beforeRange.length > 0) {
      // Anchor: extend last known price to start of range so chart never looks empty
      pts = [{recorded_at: new Date(cutoff).toISOString(), price: beforeRange[beforeRange.length-1].price}, ...inRange];
    }
    // Also append a "now" point at latest price to show current state
    if (pts.length > 0 && pts[pts.length-1]) {
      const lastMs = new Date(pts[pts.length-1].recorded_at).getTime();
      if (Date.now() - lastMs > 3600000) {
        pts = [...pts, {recorded_at: new Date().toISOString(), price: pts[pts.length-1].price}];
      }
    }

    const isLoading = source==='keepa' ? keepaLoading : chartLoading;
    const prices = pts.map(d=>d.price);
    const mn = prices.length ? Math.min(...prices) : 0;
    const mx = prices.length ? Math.max(...prices) : 1;
    const avg = prices.length ? prices.reduce((a,b)=>a+b,0)/prices.length : 0;
    const latest = prices[prices.length-1]||0;
    const first = prices[0]||0;
    const pctChange = first ? (latest-first)/first*100 : 0;
    const lineColor = latest < first ? '#22c55e' : latest > first ? '#ef4444' : '#6b7280';

    const W=680,H=220,PL=72,PR=16,PT=16,PB=36;
    const cw=W-PL-PR, ch=H-PT-PB;
    const pad = Math.max(mx-mn,1)*0.12;
    const yMin=mn-pad, yMax=mx+pad;
    const toX=i=>PL+(i/Math.max(pts.length-1,1))*cw;
    const toY=p=>PT+(1-(p-yMin)/(yMax-yMin))*ch;

    let path='';
    pts.forEach((d,i)=>{
      const x=toX(i),y=toY(d.price);
      if(i===0) path+=`M ${x} ${y}`;
      else { const px=toX(i-1); path+=` H ${px} V ${y} H ${x}`; }
    });

    const yTicks=4;
    const tickVals=Array.from({length:yTicks},(_,i)=>yMin+(yMax-yMin)*i/(yTicks-1));
    const xCnt=Math.min(pts.length,5);
    const xTicks=xCnt>1?Array.from({length:xCnt},(_,i)=>Math.round(i*(pts.length-1)/(xCnt-1))):pts.length?[0]:[];
    const fP=p=>'₹'+Math.round(p).toLocaleString('en-IN');
    const fD=s=>{const d=new Date(s);return d.toLocaleDateString('en-IN',{day:'numeric',month:'short'});};
    const hovPt=hov!==null?pts[hov]:null;

    const pill=(active,children,onClick,dim)=>(
      <button onClick={onClick} style={{padding:'5px 11px',borderRadius:6,border:'1px solid '+(active?'var(--text,#1a1917)':'var(--border,#e2e0d8)'),
        background:active?'var(--text,#1a1917)':'transparent',
        color:active?'#fff':dim?'var(--text3,#9b9890)':'var(--text2,#6b6960)',
        fontSize:11,fontWeight:600,cursor:dim?'default':'pointer',
        fontFamily:'DM Sans,sans-serif',opacity:dim?.45:1}}>{children}</button>
    );

    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.58)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}
        onClick={()=>setChartProduct(null)}>
        <div style={{background:'var(--surface,#fff)',borderRadius:18,padding:28,width:760,maxWidth:'96vw',
          boxShadow:'0 32px 80px rgba(0,0,0,.22)'}} onClick={e=>e.stopPropagation()}>

          {/* Header */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
            <div style={{flex:1,marginRight:16}}>
              <div style={{fontWeight:700,fontSize:14,color:'var(--text,#1a1917)',lineHeight:1.4,marginBottom:3}}>{chartProduct.name}</div>
              <div style={{fontSize:11,color:'var(--text3,#9b9890)',fontFamily:'DM Mono,monospace'}}>{chartProduct.asin}</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
              <div style={{display:'flex',background:'var(--surface2,#f0efe9)',borderRadius:8,padding:3,gap:2}}>
                {[['keepa','Keepa'],['ours','Tracked']].map(([s,l])=>(
                  <button key={s} onClick={()=>{setSource(s);setRange('3M');setHov(null);}}
                    style={{padding:'5px 14px',borderRadius:6,border:'none',fontSize:11,fontWeight:600,
                      cursor:'pointer',fontFamily:'DM Sans,sans-serif',transition:'all .15s',
                      background:source===s?'var(--surface,#fff)':'transparent',
                      color:source===s?'var(--text,#1a1917)':'var(--text3,#9b9890)',
                      boxShadow:source===s?'0 1px 4px rgba(0,0,0,.1)':'none'}}>{l}</button>
                ))}
              </div>
              <button onClick={doRefresh} title="Refresh data"
                style={{border:'none',background:'var(--surface2,#f0efe9)',borderRadius:8,
                  width:32,height:32,cursor:'pointer',fontSize:14,color:'var(--text3,#9b9890)'}}>↻</button>
              <button onClick={()=>setChartProduct(null)}
                style={{border:'none',background:'var(--surface2,#f0efe9)',borderRadius:8,
                  width:32,height:32,cursor:'pointer',fontSize:14,color:'var(--text3,#9b9890)'}}>✕</button>
            </div>
          </div>

          {isLoading ? (
            <div style={{textAlign:'center',padding:70,color:'var(--text3,#9b9890)',fontFamily:'DM Sans,sans-serif',fontSize:13}}>Loading...</div>
          ) : pts.length < 2 ? (
            <div style={{textAlign:'center',padding:'50px 0'}}>
              <div style={{fontSize:32,marginBottom:12}}>📊</div>
              <div style={{fontSize:13,fontWeight:600,color:'var(--text,#1a1917)',fontFamily:'DM Sans,sans-serif',marginBottom:6}}>
                {source==='ours'?'Not enough tracking data yet':'No data for this range'}
              </div>
              <div style={{fontSize:12,color:'var(--text3,#9b9890)',fontFamily:'DM Sans,sans-serif'}}>
                {source==='ours'?'We track prices every 5 min — check back soon.':'Try a wider range.'}
              </div>
            </div>
          ) : (<>
            {/* Stats */}
            <div style={{display:'flex',gap:8,marginBottom:16}}>
              {[
                {l:'Current',v:fP(latest),c:'var(--text,#1a1917)'},
                {l:'Change',v:(pctChange>=0?'+':'')+pctChange.toFixed(1)+'%',c:pctChange<0?'#16a34a':'#dc2626'},
                {l:'Low',v:fP(mn),c:'#2563eb'},
                {l:'High',v:fP(mx),c:'#9333ea'},
                {l:'Avg',v:fP(avg),c:'#d97706'},
              ].map(s=>(
                <div key={s.l} style={{flex:1,background:'var(--surface2,#f0efe9)',borderRadius:10,padding:'10px 12px'}}>
                  <div style={{fontSize:9,color:'var(--text3,#9b9890)',fontFamily:'DM Sans,sans-serif',textTransform:'uppercase',letterSpacing:.5,marginBottom:3}}>{s.l}</div>
                  <div style={{fontSize:13,fontWeight:700,fontFamily:'DM Mono,monospace',color:s.c}}>{s.v}</div>
                </div>
              ))}
            </div>

            {/* Range buttons */}
            <div style={{display:'flex',gap:4,marginBottom:12,alignItems:'center'}}>
              {RANGES.map(r=>{
                const hasPts=source==='keepa'||rawAll.filter(d=>new Date(d.recorded_at).getTime()>=Date.now()-r.d*86400000).length>=1||rawAll.filter(d=>new Date(d.recorded_at).getTime()<Date.now()-r.d*86400000).length>=1;
                return <React.Fragment key={r.k}>{pill(range===r.k,r.k,()=>{if(hasPts){setRange(r.k);setHov(null);}},!hasPts)}</React.Fragment>;
              })}
              <span style={{marginLeft:'auto',fontSize:11,color:'var(--text3,#9b9890)',fontFamily:'DM Sans,sans-serif'}}>
                {pts.length} pts · {fD(pts[0].recorded_at)} – {fD(pts[pts.length-1].recorded_at)}
              </span>
            </div>

            {/* Chart */}
            <div style={{position:'relative',userSelect:'none'}}>
              <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:'auto',display:'block',overflow:'visible'}}
                onMouseLeave={()=>{setHov(null);setHovX(null);}}
                onMouseMove={e=>{
                  const rect=e.currentTarget.getBoundingClientRect();
                  const rawX=(e.clientX-rect.left)*(W/rect.width);
                  const clampedX=Math.max(PL,Math.min(W-PR,rawX));
                  setHovX(clampedX);
                  const idx=Math.round((clampedX-PL)/cw*(pts.length-1));
                  setHov(Math.max(0,Math.min(pts.length-1,idx)));
                }}>
                <defs>
                  <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={lineColor} stopOpacity=".22"/>
                    <stop offset="100%" stopColor={lineColor} stopOpacity="0"/>
                  </linearGradient>
                </defs>
                {tickVals.map((v,i)=>{
                  const y=toY(v);
                  return <g key={i}>
                    <line x1={PL} x2={W-PR} y1={y} y2={y} stroke='var(--border,#e2e0d8)' strokeWidth='1'/>
                    <text x={PL-6} y={y+4} fontSize={9} fill='var(--text3,#9b9890)' textAnchor='end' fontFamily='DM Mono,monospace'>{fP(v)}</text>
                  </g>;
                })}
                {xTicks.map((idx,i)=>(
                  <text key={i} x={toX(idx)} y={H-6} fontSize={9} fill='var(--text3,#9b9890)' textAnchor='middle' fontFamily='DM Mono,monospace'>
                    {fD(pts[idx].recorded_at)}
                  </text>
                ))}
                <path d={path+` V ${PT+ch} H ${PL} Z`} fill='url(#chartFill)'/>
                <path d={path} fill='none' stroke={lineColor} strokeWidth='2.5' strokeLinejoin='round'/>
                {hovX!==null&&(
                  <line x1={hovX} x2={hovX} y1={PT} y2={PT+ch} stroke='var(--text3,#9b9890)' strokeWidth='1' strokeDasharray='3,3' opacity='.6'/>
                )}
                {hovPt&&hovX!==null&&(()=>{
                  const dotX=toX(hov),dotY=toY(hovPt.price);
                  const tipW=152,tipH=52,tipX=Math.min(hovX+10,W-PR-tipW),tipY=Math.max(PT,dotY-tipH/2);
                  return <g>
                    <circle cx={dotX} cy={dotY} r={4} fill={lineColor} stroke='var(--surface,#fff)' strokeWidth='2'/>
                    <circle cx={dotX} cy={dotY} r={9} fill={lineColor} opacity='.12'/>
                    <rect x={tipX} y={tipY} width={tipW} height={tipH} rx={8} fill='var(--text,#1a1917)'/>
                    <text x={tipX+10} y={tipY+17} fontSize={10} fill='#9b9890' fontFamily='DM Sans,sans-serif'>{fD(hovPt.recorded_at)}</text>
                    <text x={tipX+10} y={tipY+36} fontSize={14} fontWeight='700' fill='#fff' fontFamily='DM Mono,monospace'>{fP(hovPt.price)}</text>
                  </g>;
                })()}
              </svg>
            </div>

            {/* Footer */}
            <div style={{marginTop:10,fontSize:11,color:'var(--text3,#9b9890)',fontFamily:'DM Sans,sans-serif',display:'flex',justifyContent:'space-between'}}>
              <span>{source==='keepa'?'Source: Keepa price tracker (Amazon.in)':'Our tracking · prices recorded every 5 min'}</span>
              {source==='ours'&&<span>Total: {chartData.length} records</span>}
            </div>
          </>)}
        </div>
      </div>
    );
  };
  return (
    <div>
      <ChartModal />
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        <div style={{display:'flex',alignItems:'center',gap:8,background:'var(--surface,#fff)',border:'1px solid var(--border,#e2e0d8)',borderRadius:6,padding:'7px 12px',flex:1,maxWidth:300}}>
          <span style={{color:'#9b9890'}}>search</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search products"
            style={{border:'none',background:'none',outline:'none',fontSize:13,color:'var(--text,#1a1917)',width:'100%'}} />
        </div>
        <button onClick={()=>setShowAdd(true)} style={{padding:'7px 14px',borderRadius:6,fontSize:12,fontWeight:500,background:'#1a1917',color:'#fff',border:'none'}}>+ Add product</button>
        <button onClick={load} style={{padding:'7px 14px',borderRadius:6,fontSize:12,fontWeight:500,background:refreshed?'#d1fae5':'var(--surface2,#f0efe9)',border:'1px solid '+(refreshed?'#6ee7b7':'var(--border,#e2e0d8)'),color:refreshed?'#065f46':'var(--text,#1a1917)',transition:'all .3s'}}>{refreshed ? '✓ Refreshed' : '↻ Refresh'}</button>
        {forex && <span style={{marginLeft:'auto',fontSize:11,color:'#9b9890',fontFamily:'monospace'}}>USD/INR: {forex.usd_inr} | {lastUpdated}</span>}
      </div>

      {showAdd && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'var(--surface,#fff)',border:'1px solid var(--border,#e2e0d8)',borderRadius:14,padding:28,width:440}}>
            <h3 style={{fontSize:16,fontWeight:600,marginBottom:6}}>Add product</h3>
            <p style={{fontSize:12,color:'#6b6960',marginBottom:20}}>Enter the Amazon ASIN directly (10 characters, e.g. B0F3QJLD3B).</p>
            <input value={newUrl} onChange={e=>setNewUrl(e.target.value)} placeholder="e.g. B0F3QJLD3B"
              style={{width:'100%',padding:'10px 14px',border:'1px solid #e2e0d8',borderRadius:6,fontSize:13,marginBottom:16,outline:'none'}} />
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>{setShowAdd(false);setNewUrl('');}} style={{flex:1,padding:9,border:'1px solid #e2e0d8',borderRadius:6,fontSize:13,background:'none'}}>Cancel</button>
              <button onClick={handleAdd} disabled={adding} style={{flex:2,padding:9,background:'#1a1917',color:'#fff',borderRadius:6,fontSize:13,fontWeight:500}}>
                {adding?'Adding...':'Add and start tracking'}
              </button>
            </div>
          </div>
        </div>
      )}


      <div style={{overflowX:'auto',overflowY:'auto',maxHeight:'72vh',borderRadius:10,border:'1px solid var(--border,#e2e0d8)',background:'var(--surface,#fff)'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:2200,tableLayout:'fixed'}}>
          <thead>
            <tr style={{background:'var(--surface2,#f0efe9)',borderBottom:'2px solid var(--border,#e2e0d8)'}}>
              {HEADERS.map((h,i) => {
                if (h.admin && role !== 'admin') return null;
                return <th key={i} style={{width:h.w,minWidth:h.w-10,padding:'6px 10px',textAlign:'left',fontWeight:500,color:'#6b6960',fontSize:10,textTransform:'uppercase',letterSpacing:.4,borderRight:'1px solid var(--border,#e2e0d8)',background:thBg(h.g),whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',position:'sticky',top:0,zIndex:1}}>{h.l}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {sorted().map(sec => {
              if (!sec.rows.length) return null;
              return [
                <tr key={'h'+sec.type}>
                  <td colSpan={role==='admin'?22:21} style={{background:dark?'#111110':'#e8e7e0',fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:.7,color:dark?'#555450':'#9b9890',padding:'5px 10px',borderBottom:'1px solid #e2e0d8',fontFamily:'DM Sans,sans-serif'}}>
                    {sec.label}
                  </td>
                </tr>,
                ...sec.rows.map(p => {
                  const c = p.calculations || {};
                  const inactive = sec.type === 'inactive' || sec.type === 'oos';
                  const band = p.margin_band;
                  const rowBg = inactive ? '#f0efe9' : band ? bBg[band] : 'transparent';
                  const rowFg = inactive ? '#9b9890' : band ? bFg[band] : 'inherit';
                  const td = {padding:'5px 10px',borderBottom:'1px solid rgba(0,0,0,.05)',borderRight:'1px solid rgba(0,0,0,.05)',whiteSpace:'nowrap',fontFamily:'DM Mono,monospace',fontSize:11,verticalAlign:'middle',overflow:'hidden',textOverflow:'ellipsis',color:rowFg};

                  return (
                    <tr key={p.id} style={{background:rowBg, opacity:inactive?.6:1}}>
                      <td style={{padding:"2px 4px",borderBottom:"1px solid rgba(0,0,0,.05)",borderRight:"1px solid rgba(0,0,0,.05)",verticalAlign:"middle",textAlign:"center",width:30}}>
                        <button onClick={()=>togglePin(p)} style={{width:26,height:26,borderRadius:5,border:'none',background:'transparent',cursor:'pointer',fontSize:14,color:p.pinned?'#f59e0b':'rgba(128,128,128,.4)'}}>
                          {p.pinned ? '\u{1F4CC}' : '\u{1F4CD}'}
                        </button>
                      </td>
                      <td style={{padding:"2px 4px",borderBottom:"1px solid rgba(0,0,0,.05)",borderRight:"1px solid rgba(0,0,0,.05)",verticalAlign:"middle",textAlign:"center",width:36}}>
                        <button onClick={()=>openChart(p)} style={{width:26,height:26,borderRadius:5,border:'1px solid #e2e0d8',background:'transparent',cursor:'pointer',fontSize:13}} title="Price history">&#128200;</button>
                      </td>
                      <td style={{...td,fontFamily:'DM Sans,sans-serif',fontWeight:500,fontSize:12}}>
                        {p.name}
                        {p.pinned && <span style={{marginLeft:6,background:'rgba(245,158,11,.15)',color:'#b45309',padding:'2px 6px',borderRadius:20,fontSize:10,fontWeight:600}}>Pinned</span>}
                      </td>
                      <td style={{...td,fontSize:10}}>{p.asin}</td>
                      <td style={{...td,fontSize:11,color:'var(--text2,#6b6960)'}}>{getColour(p)}</td>
                      <td style={td}>{inactive ? '\u2014' : fI(p.amazon_price)}</td>
                      <td style={td}>{inactive ? '\u2014' : fI(c.ex_gst)}</td>
                      <td style={td}>{fW(p.wt_packed)}</td>
                      <td style={td}>{inactive ? '\u2014' : fW(c.wt_chargeable)}</td>
                      <td style={td}>{inactive ? '\u2014' : fI(c.local_freight)}</td>
                      <td style={td}>{inactive ? '\u2014' : fI(c.freight)}</td>
                      <td style={td}>{inactive ? '\u2014' : fI(p.other_export_cost)}</td>
                      <td style={td}>{inactive ? '\u2014' : fI(c.total_export_cost_inr)}</td>
                      <td style={td}>{inactive ? '\u2014' : fI(c.drawback)}</td>
                      <td style={td}>{inactive ? '\u2014' : fI(c.rodtep)}</td>
                      {role==='admin' && <td style={td}>{inactive ? '\u2014' : fI(c.aff_cb)}</td>}
                      {role==='admin' && <td style={td}>{inactive ? '\u2014' : fI(c.card_cb)}</td>}
                      <td style={td}>{inactive ? '\u2014' : fI(c.net_export_cost_inr)}</td>
                      <td style={td}>
                        <span style={{display:'flex',alignItems:'center',gap:4}}>
                          $<input type="number" defaultValue={p.srp_usd||''} placeholder="0.00"
                            onBlur={e=>saveSRP(p,e.target.value)}
                            style={{background:'transparent',border:'1px dashed rgba(128,128,128,.3)',borderRadius:4,padding:'3px 6px',fontFamily:'DM Mono,monospace',fontSize:11,color:rowFg,width:72,outline:'none'}} />
                        </span>
                      </td>
                      <td style={td}>{inactive||!p.srp_usd ? '\u2014' : fI(c.net_margin_inr)}</td>
                      <td style={td}>{inactive||!p.srp_usd ? '\u2014' : fU(c.net_margin_usd)}</td>
                      <td style={td}>
                        {!inactive && p.srp_usd > 0 && band ? (
                          <span style={{padding:'3px 8px',borderRadius:20,fontSize:11,fontWeight:600,fontFamily:'DM Sans,sans-serif',background:pBg[band],color:pFg[band]}}>
                            {fP(c.net_margin_pct)}
                          </span>
                        ) : inactive ? '\u2014' : <span style={{color:'#9b9890',fontSize:11}}>set price</span>}
                      </td>
                      <td style={{...td,borderRight:'none'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div onClick={()=>toggleDash(p)} style={{width:28,height:16,background:p.dash_visible?'#22c55e':'#cccac0',borderRadius:8,position:'relative',cursor:'pointer'}}>
                            <div style={{position:'absolute',top:2,left:p.dash_visible?14:2,width:12,height:12,background:'white',borderRadius:'50%',transition:'left .2s'}}></div>
                          </div>
                          {role==='admin' && (
                            <button onClick={()=>setConfirmDel(p)} title="Remove product" style={{width:22,height:22,borderRadius:5,border:'1px solid #fecaca',background:'transparent',color:'#dc2626',cursor:'pointer',fontSize:12,lineHeight:1}}>✕</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ];
            })}
          </tbody>
        </table>
      </div>
      {confirmDel && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000,cursor:'pointer'}} onClick={()=>!deleting && setConfirmDel(null)}>
          <div style={{background:'var(--surface,#fff)', borderRadius:12, padding:24, maxWidth:400, width:'90%'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15, fontWeight:700, marginBottom:8, fontFamily:'DM Sans,sans-serif', color:'var(--text,#1a1917)'}}>Remove this product?</div>
            <div style={{fontSize:13, color:'#6b6960', marginBottom:6, fontFamily:'DM Sans,sans-serif'}}>{confirmDel.name}</div>
            <div style={{fontSize:12, color:'#9b9890', fontFamily:'DM Mono,monospace', marginBottom:20}}>{confirmDel.asin}</div>
            <div style={{fontSize:12, color:'#dc2626', marginBottom:20, fontFamily:'DM Sans,sans-serif'}}>This permanently removes it from tracking and alerts. This cannot be undone from here.</div>
            <div style={{display:'flex', gap:10}}>
              <button onClick={doDelete} disabled={deleting} style={{padding:'9px 20px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'DM Sans,sans-serif', border:'1px solid #dc2626', background:'transparent', color:'#dc2626', opacity:deleting?.6:1}}>{deleting?'Removing...':'Remove'}</button>
              <button onClick={()=>setConfirmDel(null)} disabled={deleting} style={{padding:'9px 20px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'DM Sans,sans-serif', border:'1px solid var(--border,#e2e0d8)', background:'none', color:'var(--text,#1a1917)', opacity:deleting?.6:1}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
